import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { usePoll } from '../../hooks/usePoll';
import { fecthHoldingTokens, fetchProjectTokens } from '../../utils';
import * as actionTypes from '../../store/actionTypes';
import CommonMenu from '../../components/commonMenu/commonMenu';
import TokenTable from '../../components/Tables/TokenTable/token.table';
import Transactions from '../Transactions/transaction.table';
import MarketStats from '../../components/MarketStats/MarketStats';
import { useTokenListing, ListingFooter } from '../../hooks/useTokenListing';

// VentureTokens listing.
//
// Now renders through the shared TokenTable - the same component behind
// SynthCryptos, XMG Tokens, EquiTrack, DCN, etc. - so this page has the same
// columns (live OBD instead of the old static TVL figure, 24h sparkline,
// Swap, Edit), the same styling, live price blink, and a working search box.
// The old hand-rolled table had its own copy of every cell, a search box that
// was wired to nothing, and inverted 1h/24h arrows.
//
// The API rows (project_tokens / holding_tokens) use token_* column names,
// so they're mapped into TokenTable's shape here.

const toRow = (each, kind) => ({
  ...each,
  // React key unique across both sources (ids overlap between the two
  // tables); `id` stays the real DB id because EditVentureToken saves by it.
  rowKey: `${kind}-${each.id}`,
  identitySymbol: each.token_symbol,
  symbol: each.token_symbol,
  name: each.token_name,
  logo: each.image,
  description: each.token_desc,
  currentPrice: Number(each.token_price) || 0,
  // holding_tokens has no price history columns; leave undefined so the
  // table shows a neutral 0% instead of an invented change.
  price1hour:
    each.price1hour !== null && each.price1hour !== undefined
      ? Number(each.price1hour)
      : undefined,
  price24hour:
    each.price24hour !== null && each.price24hour !== undefined
      ? Number(each.price24hour)
      : undefined,
  volume: Number(each.volume) || 0,
  // Detail route for this row (TokenTable honours a per-row href).
  href: kind === 'holding' ? 'holding-token' : 'VentureToken',
  // Opens EditVentureToken from TokenTable's admin Edit button.
  type: 'project'
});

const ProjectTable = () => {
  const dispatch = useDispatch();
  const { projectTokens, holdingTokens } = useSelector(
    (state) => ({
      projectTokens: state.projectTokens,
      holdingTokens: state.holdingTokens
    }),
    shallowEqual
  );

  const [searchToken, setSearchToken] = useState('');
  const [prevPrices, setPrevPrices] = useState({});
  const [blinkUpList, setBlinkUpList] = useState([]);
  const [blinkDownList, setBlinkDownList] = useState([]);

  usePoll(
    () =>
      Promise.all([
        fetchProjectTokens(dispatch, actionTypes.FETCH_PROJECT_TOKENS),
        fecthHoldingTokens(dispatch, actionTypes.FETCH_HOLDING_TOKENS)
      ]),
    5000,
    []
  );

  const rows = useMemo(
    () => [
      ...(Array.isArray(projectTokens) ? projectTokens : []).map((e) =>
        toRow(e, 'project')
      ),
      ...(Array.isArray(holdingTokens) ? holdingTokens : []).map((e) =>
        toRow(e, 'holding')
      )
    ],
    [projectTokens, holdingTokens]
  );

  // Search + pagination over every Venture + holding token.
  const listing = useTokenListing(rows, searchToken);

  // Same up/down row flash the other token listings have.
  useEffect(() => {
    const next = {};
    const up = [];
    const down = [];
    for (const r of rows) {
      next[r.identitySymbol] = r.currentPrice;
      const prev = prevPrices[r.identitySymbol];
      if (prev !== undefined && prev !== r.currentPrice) {
        (r.currentPrice > prev ? up : down).push(r.identitySymbol);
      }
    }
    setPrevPrices(next);
    if (up.length || down.length) {
      setBlinkUpList(up);
      setBlinkDownList(down);
      const t = setTimeout(() => {
        setBlinkUpList([]);
        setBlinkDownList([]);
      }, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const loaded = projectTokens?.length > 0 || holdingTokens?.length > 0;

  return (
    <div
      className="screen"
      style={{
        zIndex: '100',
        position: 'sticky',
        width: '100%',
        marginBottom: '1rem'
      }}
    >

      <div style={{ width: '95%', minWidth: '340px' }} className="res-token">

        <MarketStats />
        <CommonMenu label={'VentureTokens'} setSearch={setSearchToken} />
        <TokenTable
          loading={false}
          token={listing.pageRows}
          blinkUpList={blinkUpList}
          blinkDownList={blinkDownList}
          href={'VentureToken'}
          swappable={true}
          showPecu={false}
        />
        <ListingFooter
          listing={listing}
          loaded={loaded}
          noun="Venture tokens"
        />

        <Transactions />
      </div>
    </div>
  );
};

export default React.memo(ProjectTable);
